/**
 * TheLab — entry point: the one 2.5D world (`WorldApp25D`), its panels and its modules.
 *
 *   1. Procedural 64×64 world with biome generation
 *   2. Tap/click-to-move with A* pathfinding
 *   3. Buildings with 2.5D depth illusion
 *   4. Audio-reactive creatures
 *   5. Adaptive quality for 60fps
 *
 * @packageDocumentation
 */

import './ui/styles.css';
import type { SignalStream } from '@seppelabs/engine-listen';
import { applyChromeDirection } from './ui/chrome-direction.js';
import { InstallOffer, registerServiceWorker } from './ui/install.js';

// The browser's install offer arrives early and only once: catch it before anything awaits.
const install = new InstallOffer().listen();

// ── Build identity ───────────────────────────────────────────────────
// The deploy workflow sets VITE_BUILD_ID to the commit SHA. Baking it into the
// entry chunk makes every deploy's `assets/index-<hash>.js` a path the CDN has
// never seen, so a fallback page cached under an earlier path can never be
// served for the current build (CloudFront caches *.js as immutable).
export const BUILD_ID: string = (import.meta.env.VITE_BUILD_ID as string | undefined) ?? 'dev';
(window as unknown as { __labBuild?: string }).__labBuild = BUILD_ID;

// ── The realm (spec/people PR2) ───────────────────────────────────────
// The portal's door navigates to the realm app: its own dev origin under this app's own
// dev server, and the same-origin `/realm/` path assumed for production until deploy
// decides (A16 keeps a session bound to origin, so any other host costs a second
// sign-in). VITE_RPG_URL overrides both.
export const REALM_URL: string =
  (import.meta.env.VITE_RPG_URL as string | undefined) ??
  (import.meta.env.DEV ? 'http://127.0.0.1:5178/' : '/realm/');

const urlParams = new URLSearchParams(window.location.search);

// The chrome's direction (U3, W11): one attribute on the root rebinds the whole
// geometry ladder in `styles.css`. `card` unless the URL asks otherwise.
applyChromeDirection(urlParams);

async function main(): Promise<void> {
  const root = document.getElementById('root');
  if (!root) throw new Error('Missing #root');

  // The app shell, for the install and for opening while offline: production only (a dev
  // server is never shadowed by a cached page), never for a frame server, and before
  // anything awaits — the browser's install check waits on it.
  if (import.meta.env.PROD && !urlParams.has('render')) void registerServiceWorker();

  // `?render=1`: a frame server for `scripts/slsig-render.mjs` — no loop, no
  // mic, no panels; `window.__labRender` steps and captures (render-mode.ts).
  const { isRenderMode, bootRenderMode } = await import('./render-mode.js');
  if (isRenderMode(urlParams)) {
    await bootRenderMode(urlParams);
    return;
  }

  // The lab's own beacon while the world is being made (props/polish.json `loading-screen`);
  // it goes when the world is ready, and also when booting fails — a stuck screen would hide
  // whatever the page has to say.
  const { mountBoot } = await import('./ui/boot.js');
  const boot = mountBoot();
  try {
    await boot25D();
  } finally {
    boot.done();
  }
}

// ── 25D Mode (new tile-based world) ─────────────────────────────────

async function boot25D(): Promise<void> {
  const { WorldApp25D } = await import('./world/WorldApp25D.js');
  const { moodMode } = await import('./world/mood.js');

  const audioContext = new AudioContext();

  // What the world listens to (spec/lab-world.md L19, amendment proposed). One call, one
  // ladder: the visitor's choice — the lab's music (the default), the rig on this machine over
  // loopback, music they hand over from this device, a `.slsig` link, their microphone, or
  // quiet — and every way any of those can fail landing on the lab's own music rather than on
  // silence, because silence grows no garden and L43 makes the garden the only mint. A query
  // parameter still wins for the harnesses and the frame server; nothing in the product uses one.
  const { onChosenChange, openWorldSignal } = await import('./audio/signal-source.js');
  const opened = await openWorldSignal({ params: urlParams, dev: import.meta.env.DEV });
  const { currentListening, onListeningChange } = await import('./ui/listening.js');
  const listeningMode = currentListening();
  const programme = opened.programme;
  const setNow = opened.playing.kind === 'set' ? opened.playing.set : null;
  const signal: SignalStream | undefined = opened.stream;
  const silent = opened.playing.kind === 'silent';
  if (opened.socket) (window as unknown as Record<string, unknown>).__signalSocket = opened.socket;
  console.log(`[TheLab] ${describeOpened(opened)}`);
  // A new choice restarts the run with the new source (L19): the world hash is the run's.
  onListeningChange(() => window.location.reload());
  onChosenChange(() => window.location.reload());

  // The game's flow (spec/live-world, proposed LV1/LV2): the boot is the calm world — no
  // readout, no notifier actor, no beat hop — and `?play=on` brings the game back, gate by
  // gate. Every gate below reads this one line.
  const playFlow = urlParams.get('play') === 'on';

  const app = new WorldApp25D({
    seed: 42,
    worldWidth: 64,
    worldHeight: 64,
    audioContext,
    signal,
    playFlow,
    // LV3: the sky's wash — the URL's `?mood=`, defaulting to the whole cycle (starting at
    // dusk). Render mode and the harnesses keep their own default: off.
    mood: moodMode(urlParams),
  });

  await app.init();

  // Expose to console
  (window as unknown as Record<string, unknown>).__world = app;
  (window as unknown as Record<string, unknown>).__lab = app;

  // `?grid=on|off`: pin the world grid past the quality budget, which otherwise
  // writes its own answer back once a second (L27, W24). Absent, the budget rules.
  const grid = urlParams.get('grid');
  if (grid === 'on' || grid === 'off') app.grid = grid === 'on';

  // `?record=1`: a button that saves the live run as a `.slsig` file — the
  // shortest path from a show to `scripts/slsig-render.mjs`.
  if (urlParams.get('record') === '1' && !signal) {
    const { mountRecordButton } = await import('./record-button.js');
    mountRecordButton(app);
  }

  // Panels: one registry and one host for every room below. A tap on a room
  // opens the module registered under the room's id (spec/lab-world.md L6).
  const { ModuleRegistry } = await import('./modules/ModuleRegistry.js');
  const { PanelHost } = await import('./ui/PanelHost.js');
  const { bootSettings } = await import('./modules/settings-module.js');
  const registry = new ModuleRegistry();
  const panels = new PanelHost();
  app.onBuildingTap = (id) => {
    const meta = registry.get(id);
    if (meta) panels.open(meta);
  };
  // The portal is the one prop with a door (spec/people PR3): its tap is consumed, the
  // card is loaded on the first tap (the same lazy chunk dressWorld reaches for), and
  // every other identified prop — the worksite — declines and falls through to the walk.
  // The id string is the catalog's own `catalogPropId('portal')`; a test pins the two
  // together so the namer and this wiring cannot drift apart.
  const PORTAL_ID = 'catalog:portal';
  app.onPropTap = (id) => {
    if (id !== PORTAL_ID) return false;
    void import('./ui/realm-door.js').then(({ openRealmDoor }) =>
      openRealmDoor({ url: REALM_URL }),
    );
    return true;
  };

  // Hoisted so the server picker can join it once the session exists (below); the panel
  // reads these options each time it opens.
  const settingsOpts: import('./modules/settings-module.js').SettingsOptions = {
    buildId: BUILD_ID,
    install,
    play: playFlow,
    listening: {
      mode: listeningMode,
      set: setNow,
      programme,
      playing: opened.playing,
      ...(opened.fellBack ? { fellBack: opened.fellBack } : {}),
    },
  };
  const settingsMeta = bootSettings(app, registry, settingsOpts);
  (window as unknown as Record<string, unknown>).__panels = panels;

  // How the visitor walks on a touch screen (spec/lab-world.md §12): tapping, or the pad
  // at the bottom. The choice is theirs and lives in this browser; the world only follows.
  const { currentControls, onControlsChange, controlsAskDue, mountControlsAsk } = await import(
    './ui/controls.js'
  );
  const { WalkPad } = await import('./ui/walk-pad.js');
  const pad = new WalkPad();
  const applyControls = (mode: 'tap' | 'pad'): void => {
    app.tapToWalk = mode === 'tap';
    if (mode === 'pad') {
      pad.mount(document.body);
      app.pad = pad;
    } else {
      pad.unmount();
      app.pad = null;
    }
  };
  applyControls(currentControls());
  onControlsChange(applyControls);
  window.addEventListener('blur', () => pad.release());
  (window as unknown as Record<string, unknown>).__pad = pad;

  // The game's readout (architecture/game-and-show.md §4, spec/look.md Q11): the charges in
  // hand, the rooms lit out of the rooms there are, the garden still standing, and the verb
  // offered where the visitor stands, with its progress drawn as frames. It mounts only once
  // the world carries a play layer (`playState`/`playPrompt`), so this line is true both
  // before that lands and after — and it is the game's counters, never the lab's own numbers,
  // which live in the drawer at the other corner.
  const { isPlayWorld } = await import('./ui/play-types.js');
  if (import.meta.env.DEV && urlParams.get('hud') === 'demo') {
    // `?hud=demo` (dev only): a pretend play layer, to look at the readout — the same shape
    // as `?treasury=demo` and `?store=demo`. It force-mounts over either world (it used to
    // be unreachable: the first branch below always won) — the calm world's way back to the
    // readout (spec/live-world, proposed LV2), and `e2e/hud-shot.mjs` steps it by hand.
    const { mountDemoHud } = await import('./ui/hud-demo.js');
    (window as unknown as Record<string, unknown>).__hud = await mountDemoHud(document.body);
    console.log('  The play readout is up on a pretend play layer (?hud=demo)');
  } else if (playFlow && isPlayWorld(app)) {
    const { mountHud } = await import('./ui/hud.js');
    (window as unknown as Record<string, unknown>).__hud = mountHud(app, document.body);
    console.log('  The play readout is up (console: __hud)');
  }

  // When the world hears nothing, it says so (L19, L43, `ui/signal-notice.ts`). A stream that
  // carries nothing grows no garden and mints no charge, and a game that says nothing about
  // that looks broken rather than unstarted — which is exactly how the first playtest read it.
  // Only *quiet*, or a signal that would not load at all, gets here.
  const notice = await import('./ui/signal-notice.js');
  if (silent) {
    (window as unknown as Record<string, unknown>).__signalNotice = notice.mountSignalNotice(
      document.body,
      opened.playing.kind === 'silent' ? opened.playing.cause : 'unheard',
    );
    console.log('  The world hears nothing; the page says so (console: __signalNotice)');
  } else if (notice.namesASource(opened)) {
    // …and when it does hear something, it says what (L19's amendment, proposed): the source is
    // named on screen, so nobody has to guess whether the thing they are hearing is the thing
    // the world is hearing, and a source that fell back says so instead of hiding it.
    (window as unknown as Record<string, unknown>).__signalSource = notice.mountSignalSourceLine(
      document.body,
      opened,
      {
        onChange: () => panels.open(settingsMeta),
        ...(opened.watchRig ? { watch: opened.watchRig } : {}),
      },
    );
  }

  // Who is at the keyboard (ADR-0015): the front desk inside the entrance signs
  // you in; the Treasury below answers your key; Admin is the owner's room.
  const { createSession } = await import('./modules/session.js');
  const { bootFrontDesk } = await import('./modules/front-desk-module.js');
  const { bootTreasury } = await import('./modules/treasury-module.js');
  const { bootAdmin } = await import('./modules/admin-module.js');
  const session = createSession();
  // A16: a kept sign-in reopens without a password, once each server confirms it
  {
    const { restoreAll } = await import('./modules/session.js');
    void restoreAll(session);
  }
  const openFrontDesk = (): void => {
    const desk = registry.get('entrance');
    if (desk) panels.open(desk);
  };
  bootFrontDesk(session, registry, { inviteToken: urlParams.get('invite') });
  // `?treasury=demo` (dev only): the panel with sample numbers, to look at the animation.
  const demo = import.meta.env.DEV && urlParams.get('treasury') === 'demo';
  bootTreasury(session, registry, { world: app, openFrontDesk, demo });
  bootAdmin(session, registry, { openFrontDesk });
  // The workbench (16C): this space's experiments, behind the session's own token.
  {
    const { bootWorkbench } = await import('./workbench/workbench-module.js');
    bootWorkbench(session, registry);
  }
  if (urlParams.get('invite')) openFrontDesk();
  (window as unknown as Record<string, unknown>).__session = session;

  // Playing offline (spec/world-sync.md WS5, WS12): the page says so, and any world synced
  // with `OfflineWorld` adds its waiting and refused moves. Out of the page while online with
  // nothing waiting, so no harness shot changes. `__offline` registers a world (e2e, console).
  {
    const { mountOfflineBadge } = await import('./ui/offline-badge.js');
    const { OfflineWatch } = await import('./modules/offline-sync.js');
    const watch = new OfflineWatch();
    const badge = mountOfflineBadge(document.body, {
      onDismiss: () => void watch.dismissRefused(),
    });
    watch.onChange((s) => badge.update(s));
    (window as unknown as Record<string, unknown>).__offline = watch;
  }

  // The Lab as a place is a whole screen: the side menu (booted below) steps aside when it
  // opens, so nothing of the world's chrome lies over the yard or the house.
  let sideAsideForPlace: (() => void) | null = null;
  /** Her Lab, while this origin's front door names a space (16D); the place itself. */
  let place: import('./place/place-module.js').PlaceHandle | null = null;
  /**
   * The one house conversation every view shares (CP2) — the room, the assistant's chat
   * tab, the companion rail — built on first use so its history ask lands after sign-in.
   */
  let houseTalk: (() => import('./house/house-talk.js').HouseTalk) | null = null;

  // Which server (souls-and-servers.md §2, N1/N2/N6): Settings → Server. `__server` drives
  // the same picker from the console and the e2e scripts.
  {
    const { ServerPicker } = await import('./ui/server-picker.js');
    const picker = new ServerPicker({ session });
    settingsOpts.server = picker;
    (window as unknown as Record<string, unknown>).__server = picker;

    // The Lab as a place (15P): the server you stand in drawn as its Lab — each module a
    // building, the Hermes house one of them, a portal per other server. `L` from the world,
    // `?place=open`, Settings → Server → "Visit its Lab"; the app's home when you chose a
    // server of your own. Out of the page while closed.
    const { bootPlace } = await import('./place/place-module.js');
    const { servers } = await import('./modules/endpoints.js');
    const { HouseClient } = await import('./house/house-client.js');
    const { HouseTalk } = await import('./house/house-talk.js');
    let sharedTalk: import('./house/house-talk.js').HouseTalk | null = null;
    houseTalk = () => {
      if (!sharedTalk) sharedTalk = new HouseTalk(new HouseClient(session));
      return sharedTalk;
    };
    const p = bootPlace({
      session,
      check: () => picker.check(),
      houseTalk: houseTalk ?? undefined,
      onOpen: () => sideAsideForPlace?.(),
      openModule: (id) => {
        const meta = registry.get(id);
        if (!meta) return false;
        panels.open(meta);
        return true;
      },
    });
    place = p;
    picker.visitLab = (id) => {
      panels.close();
      p.open(id);
    };
    (window as unknown as Record<string, unknown>).__place = p;
    // the page's own server list, for the harnesses' fixture Lab (one module instance: a
    // harness importing endpoints.ts by path would get a second, empty registry)
    (window as unknown as Record<string, unknown>).__servers = servers;
    if (urlParams.get('place') === 'open' || servers.home.id !== '') p.open();
  }

  // The side menu (spec/lab-world.md §13): who you are, your money, your jobs — at the
  // right edge wherever you stand; docked on a wide screen, a sheet on a phone.
  const { bootSideMenu } = await import('./modules/side-menu-module.js');
  const { TREASURY_ID } = await import('./world/levels.js');
  const side = bootSideMenu(document.body, {
    session,
    openFrontDesk,
    openTreasury: () => {
      const t = registry.get(TREASURY_ID);
      if (t) panels.open(t);
    },
    demo,
  });
  (window as unknown as Record<string, unknown>).__side = side;
  sideAsideForPlace = () => side.setOpen(false, false);
  if ((window as unknown as { __place?: { isOpen: boolean } }).__place?.isOpen) sideAsideForPlace();

  // The companion (spec/companion.md CP1–CP4): the chip on the right edge, the rail over
  // the Lab, the shutter — a view over the same talk the room and the chat tab have (CP2).
  if (houseTalk) {
    const { bootCompanion } = await import('./companion/companion.js');
    bootCompanion(document.body, { talk: houseTalk, side });
  }

  // Her address (16D): the front door of *this origin* may name the space its Host selected
  // (SP3). That document is the only source — never a Host this page can see (S1, S6) — and
  // when it names one the app is *there*: server = this origin, trusted `private`, first run
  // (invite or sign-in, accounts.md A8) until there is a session, then her Lab's yard.
  let atHerLab = false;
  {
    const { bootGuestSpace } = await import('./space/guest-space.js');
    const { mountFirstRun } = await import('./space/first-run.js');
    const { servers } = await import('./modules/endpoints.js');
    const openHerLab = (): void => {
      // nothing of the world's chrome (or an `?invite` desk) lies over her Lab: at her
      // address the yard is the first thing on screen, every time
      panels.close();
      if (!place?.isOpen) place?.open();
    };
    const guest = await bootGuestSpace({
      session,
      registry: servers,
      openLab: openHerLab,
      showFirstRun: (space) => {
        panels.close();
        const run = mountFirstRun({
          space,
          session,
          inviteToken: urlParams.get('invite'),
          onSignedIn: () => {
            panels.close();
            openHerLab();
          },
        });
        (window as unknown as Record<string, unknown>).__firstRun = run;
      },
    });
    atHerLab = guest.space !== null;
    if (guest.space) (window as unknown as Record<string, unknown>).__space = guest.space;
    console.log(
      guest.space
        ? `[TheLab] this origin is space "${guest.space.id}" (${guest.space.name}): ${
            guest.firstRun ? 'first run, then her Lab' : 'her Lab is open'
          }`
        : '[TheLab] this origin names no space; the app boots as it always has',
    );
  }

  // The setup room (spec/setup.md SU8): the first run of a local gateway is a stack with
  // no owner, no world and no bindings yet, and the entrypoint is where that gets fixed.
  // One status call when a session lands, and only an unconfigured server opens it — a
  // configured one costs a single request and shows nothing. A space-named origin routes
  // through mountFirstRun above, which reaches the same room; this is the every-origin
  // path (the lab-endpoint stack has no space).
  {
    const { createSetupLine, trustDoorOrigin } = await import('./setup/setup-client.js');
    const { mountSetupRoom } = await import('./setup/setup-room.js');
    const { servers } = await import('./modules/endpoints.js');
    let setupSeen = false;
    const maybeSetup = (s: { status: string }): void => {
      if (setupSeen || s.status !== 'in') return;
      setupSeen = true;
      const line = createSetupLine({ session });
      void line.status().then((answer) => {
        if (answer.kind !== 'ok') return;
        // a status answer is the machine's own door (SU5): this origin is this
        // machine's gateway, so the home server may hold friend-sensitive calls —
        // without it the house's turns refuse against the build's `public` default
        trustDoorOrigin(servers);
        if (answer.answer.configured) return;
        const room = mountSetupRoom({ line });
        (window as unknown as Record<string, unknown>).__setupRoom = room;
      });
    };
    maybeSetup(session.state);
    session.onChange((s) => maybeSetup(s));
  }

  // Things in the world (props/README.md): the catalog's props stand in front of their
  // rooms once the world file has placed every building, the terrain stands on the tiles the
  // world seed picks (9B.5), and the feedback layer gets its strips.
  const { dressWorld } = await import('./world/prop-catalog.js');
  void app.worldSettled.then(() => dressWorld(app));

  // The street (architecture/houses.md): a house per account with a plot, free plots
  // everywhere else; laid out from the accounts server, again on every sign-in.
  const { bootHouses } = await import('./modules/houses-module.js');
  void app.worldSettled.then(() => bootHouses(session, app, registry, { openFrontDesk }));

  // The notifier (architecture/notifier.md): the character below that walks up to you and
  // says how your money is doing, and which jobs are waiting. Presentation only. It boots once
  // the world has settled, as a promise the store's notices wait on too (L37); `__notifier`
  // stays on the window for the e2e scripts.
  const { bootNotifier } = await import('./modules/notifier-module.js');
  const notifierReady = app.worldSettled.then(() => {
    // calm (LV1): the module still boots — its board, its sources, `__notifier` for the
    // console and the harnesses — but the actor stays out of the lab and nothing polls
    const notifier = bootNotifier(app, session, { demo, autostart: playFlow });
    (window as unknown as Record<string, unknown>).__notifier = notifier;
    return notifier;
  });

  // The run has an end (plan 9B.6, L40/L46): when the last room's lamp goes live the notifier
  // mentions it once, in the world, beside the lamps standing lit together — no modal, no score
  // screen, and the run keeps running. Presentation: it reads the play layer and moves no hash.
  // Calm (LV1) skips it — a voice nobody hired to speak is not the live world's.
  if (playFlow) {
    void notifierReady
      .then(async (notifier) => {
        const { watchRoomsLit } = await import('./world/npc/lit-notice.js');
        (window as unknown as Record<string, unknown>).__lit = watchRoomsLit(app, notifier);
      })
      .catch(() => {
        /* no notifier, no voice: the lamps still stand lit together */
      });
  }

  // First visit: how the place works, once — with the walking question and the install
  // offer on a phone. Later visits ask the walking question alone until it is answered.
  // At her address (16D) the page is the invitation instead; the CSS keeps the world's
  // cards out of first run and out of the yard alike.
  const { mountGuide } = await import('./ui/guide.js');
  const { renderInstallOffer } = await import('./ui/install.js');
  if (!atHerLab && !mountGuide(document.body, { install, play: playFlow }) && controlsAskDue()) {
    mountControlsAsk(document.body, { extra: renderInstallOffer(install) });
  }

  // Mesh Forge, the first generation.mesh consumer. Boots whenever a world can
  // be fetched from the worlds API (dev middleware, or wherever
  // VITE_SEPPELABS_WORLDS_URL points); absent, quietly, otherwise.
  try {
    const { bootMeshForge } = await import('./modules/mesh-forge-module.js');
    const forge = await bootMeshForge(app, urlParams, { registry, host: panels });
    if (forge) {
      (window as unknown as Record<string, unknown>).__forge = forge;
      console.log('  Tap the Mesh Forge building → its panel');
      console.log('  Console: __forge.open(), __forge.forgeFromUrl("/api/dev/sample.png")');
      console.log('  URL: ?forge=/api/dev/sample.png or ?artifact=sha256:<digest>');
    }
  } catch (err) {
    console.warn('[TheLab] Mesh Forge not available:', err);
  }

  // Phase 8 8B.1: the Lab Console
  // At the LLM Station (placed below in DEV) and on Ctrl+`: a model that reads the buildings,
  // walks you to a door and opens a room, and — on the dev server, over loopback, after the dev
  // sign-in (W26) — reads, writes and runs in the checkout. Dev only, like its building and its API.
  if (import.meta.env.DEV) {
    const { bootConsole } = await import('./modules/console-module.js');
    const labConsole = bootConsole(app, session, { registry, panels });
    (window as unknown as Record<string, unknown>).__console = labConsole;
    console.log('  Ctrl+` (or tap the LLM Station below) → the Lab Console');
  }

  // Phase 8 8B.2: the music studio
  // A tap on `music-studio` opens it; its audio waits for a gesture inside the panel. Peers
  // meet over the dev relay (or VITE_SIGNAL_URL), else over a BroadcastChannel.
  {
    const { bootStudio } = await import('./modules/studio-module.js');
    const relay =
      (import.meta.env.VITE_SIGNAL_URL as string | undefined) ??
      (import.meta.env.DEV
        ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/signal`
        : undefined);
    const studio = bootStudio(app, { registry, panels, signalURL: relay });
    (window as unknown as Record<string, unknown>).__studio = studio;
    console.log('  Below: tap the Music Studio -> the sequencer (console: __studio.open())');
  }

  // Phase 8 8B.3: terminal
  const { bootTerminal } = await import('./modules/terminal-module.js');
  const terminal = bootTerminal(app, session, { registry, panels, side, openFrontDesk });
  (window as unknown as Record<string, unknown>).__terminal = terminal;
  console.log('  Ctrl+T → the terminal (help, whoami, status, ls, goto <building>)');

  // Phase 8 8B.4: store
  // The store (plan W25, L34–L37): the manifests the registry can install, each a building on a
  // free plot of the street below, over the accounts session and the worlds file. Dev lists the
  // fixtures; `?store=demo` (dev only) installs end to end against a pretend world on this page;
  // `?store=open` opens the panel. It boots with the notifier it tells about an install.
  void notifierReady
    .catch((err: unknown) => {
      console.warn('[TheLab] the notifier did not boot; the store runs without its notices:', err);
      return null;
    })
    .then(async (notifier) => {
      const { bootStore } = await import('./modules/store-module.js');
      const store = bootStore(app, session, {
        registry,
        panels,
        side,
        notifier,
        openFrontDesk,
        fixtures: import.meta.env.DEV,
        demo: import.meta.env.DEV && urlParams.get('store') === 'demo',
      });
      (window as unknown as Record<string, unknown>).__store = store;
      if (urlParams.get('store') === 'open') store.open();
    });

  console.log('[TheLab] 25D world ready!');
  console.log('  Click/tap → move avatar (or the pad, on a touch screen: Settings)');
  console.log('  WASD → walk');
  console.log('  Scroll → zoom');
  console.log('  Ctrl+D → debug overlay');
  console.log('  Right-click → cancel movement');
  console.log('');
  console.log('  Console: __lab.addBuilding("music-studio", 10, 10, 3, 3)');
  console.log('  Console: __lab.removeBuilding("music-studio")');
  console.log('  Console: __lab.setLevel(-1)  (the lab below; visitors take the stairs)');
  console.log(
    '  Below: tap the Settings room → where you are, this run, pause the mic, save as .slsig',
  );
  console.log('  Walk into the entrance → the front desk (sign in, or ?invite=<token>)');
  console.log("  Below: the Treasury answers your key; Admin is the owner's room");
  console.log('  Below: Settings → Listening → the rig in this room, or music you give it');
  console.log('  URL: ?slsig=/dev/beat.slsig  (dev only; the product needs no parameter)');
  console.log(
    '  URL: ?signal=ws://127.0.0.1:8793  (the Listener on this machine; Settings offers it too)',
  );
  console.log('  Console: __lab.exportRecording()  (the live run as .slsig bytes)');
  console.log('  URL: ?record=1  (a Save .slsig button for the live run)');
  console.log(
    '  URL: ?render=1&slsig=<url>&seed=42&fps=60&w=1080&h=1920[&level=-1]  (frame server; scripts/README.md)',
  );
}

/**
 * One console line saying what the world is listening to and what it fell back from. The page
 * says the same thing on screen (`ui/signal-notice.ts`); this is for a developer's log, and for
 * the harnesses, which read it when a run does not grow.
 */
function describeOpened(opened: {
  playing: { kind: string };
  chosen: { kind: string };
  fellBack?: { from: string; why: string };
  stream?: unknown;
}): string {
  const stream = opened.stream as { digest?: string; frameCount?: number } | undefined;
  const bits = [`signal: ${opened.playing.kind} (chose ${opened.chosen.kind})`];
  if (stream?.frameCount) bits.push(`${stream.frameCount} frames`);
  if (stream?.digest) bits.push(`digest ${stream.digest.slice(0, 12)}`);
  if (opened.fellBack) bits.push(`fell back from ${opened.fellBack.from}: ${opened.fellBack.why}`);
  return bits.join(' \u00b7 ');
}

main().catch(console.error);
